import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Form, Dropdown, ButtonGroup, Modal } from 'react-bootstrap';
import { FaTimesCircle } from "react-icons/fa";
import { IoEye } from "react-icons/io5";
import { MdOutlineNotInterested } from "react-icons/md";
import './rejected-leave.css';
import LeaveLayout from '../../components/LeaveLayout.jsx';

export default function RejectedLeave() {
  const [showModal, setShowModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(""); // Track search query
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  // Function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Intl.DateTimeFormat('en-US', options).format(new Date(dateString));
  };

  // Fetch rejected leaves on component mount and when search query or pagination changes
  useEffect(() => {
    const fetchRejectedLeaves = async () => {
      try {
        const response = await fetch(`https://backend.ems.uk.hiradentallab.com/api/leaves/leaves/rejected?search=${searchQuery}`);
        if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
        }
        const data = await response.json();
        setTableData(data.rejectedLeaves || []); // Ensure it handles empty arrays gracefully
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchRejectedLeaves();
  }, [searchQuery]); // Fetch when search query changes

  // Handle the eye button click
  const handleEyeClick = (rowData) => {
    setSelectedRowData(rowData);
    setShowModal(true);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Get current data to be displayed based on pagination
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = tableData.slice(indexOfFirstRow, indexOfLastRow);

  // Calculate total pages
  const totalPages = Math.ceil(tableData.length / rowsPerPage);

  // Render loading, error, or table
  if (loading) {
    return <p>Loading...</p>;
  }

  if (error) {
    return <p>Error: {error}</p>;
  }

  return (
    <LeaveLayout>
      <div>
        <h1 className="rejected-leave-header">
          <FaTimesCircle className="rejected-leave-icon" />
          Rejected Leaves
        </h1>
        <hr className="rejected-leave-divider" />
        <div className="rejected-leave-content">
          <div>
            <div className="filter-container">
              <Col xs={12} md={6} className="filter-left">
                <Dropdown className="d-inline mx-2" autoClose="inside">
                  <Dropdown.Toggle id="dropdown-autoclose-inside">
                    Show Entries
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="custom-dropdown-menu">
                    <Dropdown.Item onClick={() => setRowsPerPage(15)}>15</Dropdown.Item>
                    <Dropdown.Item onClick={() => setRowsPerPage(25)}>25</Dropdown.Item>
                    <Dropdown.Item onClick={() => setRowsPerPage(50)}>50</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
              <Col xs={12} md={6} className="filter-right">
                <Form>
                  <Form.Control
                    type="search"
                    placeholder="Search"
                    aria-label="Search"
                    value={searchQuery}
                    onChange={handleSearchChange} // Update search query dynamically
                  />
                </Form>
              </Col>
            </div>
            <div className="table-responsive">
              <table className="table table-striped">
                <thead className="rejected-leave-table-header">
                  <tr className='table-header'>
                    <th>SR</th>
                    <th>Name</th>
                    <th>Leave Type</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Applied Date</th>
                    <th>HR Approval</th>
                    <th>Admin Approval</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRows.map((row, index) => (
                    <tr key={index}>
                      <td>{indexOfFirstRow + index + 1}</td>
                      <td>{row.name}</td>
                      <td>{row.leaveType}</td>
                      <td>{formatDate(row.startDate)}</td>
                      <td>{formatDate(row.endDate)}</td>
                      <td>{formatDate(row.appliedDate)}</td>
                      <td>{row.hrAction}</td>
                      <td>{row.adminAction}</td>
                      <td>
                        <Button onClick={() => handleEyeClick(row)} size="sm">
                          <IoEye />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="table-footer">
                  <tr className="pending-leave-table-footer">
                    <td >Total Entries</td>
                    <td>{tableData.length}</td>
                    <td colSpan="7"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {/* Pagination */}
            <div className="pagination-row">
              <div className="entries-info">
                Showing{" "}
                {(currentPage - 1) * rowsPerPage + 1} to{" "}
                {Math.min(currentPage * rowsPerPage, tableData.length)} of{" "}
                {tableData.length} entries
              </div>
              <nav>
                <ul className="pagination no-gap">
                  <li
                    className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                  >
                    <a
                      href="/#"
                      className="page-link"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(currentPage - 1);
                      }}
                    >
                      Previous
                    </a>
                  </li>
                  {[...Array(totalPages)].map((_, pageIndex) => (
                    <li
                      key={pageIndex + 1}
                      className={`page-item ${
                        currentPage === pageIndex + 1 ? "active" : ""
                      }`}
                    >
                      <a
                        href="/#"
                        className="page-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(pageIndex + 1);
                        }}
                      >
                        {pageIndex + 1}
                      </a>
                    </li>
                  ))}
                  <li
                    className={`page-item ${
                      currentPage === totalPages ? "disabled" : ""
                    }`}
                  >
                    <a
                      href="/#"
                      className="page-link"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(currentPage + 1);
                      }}
                    >
                      Next
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Stylish Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered // Center the modal on the screen
        className="custom-modal"
      >
        <Modal.Header closeButton className="custom-modal-header">
          <Modal.Title>Leave Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRowData && (
            <>
              <p><strong>Name:</strong> {selectedRowData.name}</p>
              <p><strong>Leave Type:</strong> {selectedRowData.leaveType}</p>
              <p><strong>Start Date:</strong> {formatDate(selectedRowData.startDate)}</p>
              <p><strong>End Date:</strong> {formatDate(selectedRowData.endDate)}</p>
              <p><strong>Applied Duration:</strong> {selectedRowData.appliedDuration || "-"}</p>
              <p><strong>Applied Date:</strong> {formatDate(selectedRowData.appliedDate)}</p>
              <p><strong>HR Approval:</strong> <MdOutlineNotInterested style={{ color: 'red' }} /></p>
              <p><strong>Admin Approval:</strong> <MdOutlineNotInterested style={{ color: 'red' }} /></p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </LeaveLayout>
  );
}
